/**
 * Admin — Satellite Network mission-control page.
 *
 * Shows a live grid of every registered regional satellite. Each card
 * displays:
 *   - green/red status badge (based on `reachable` + `central_reachable`)
 *   - region + name
 *   - cache hit rate, requests/hr, upstream errors
 *   - last time central was reachable + last error (if any)
 *
 * Registered satellites are stored in the `satellites` MongoDB
 * collection on the central deployment.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2, RefreshCw, Trash2, Plus, Globe2, CheckCircle2, AlertTriangle, XCircle } from "lucide-react";

const REFRESH_MS = 30_000;

export default function AdminSatellites() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: "", region: "", base_url: "" });
  const [generatedAt, setGeneratedAt] = useState(null);

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await http.get("/admin/satellites/status");
      setRows(data.items || []);
      setGeneratedAt(data.generated_at);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load satellites.");
    } finally { setBusy(false); }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, REFRESH_MS);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!form.name || !form.region || !form.base_url) return toast.error("Fill all fields.");
    try {
      await http.post("/admin/satellites", form);
      toast.success("Satellite registered.");
      setShowAdd(false);
      setForm({ name: "", region: "", base_url: "" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to register.");
    }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Un-register ${name}? Its data on central is not affected.`)) return;
    try {
      await http.delete(`/admin/satellites/${id}`);
      toast.success("Removed.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to remove."); }
  };

  if (!user?.is_admin && !user?.is_super_admin) {
    return <div className="max-w-3xl mx-auto p-8 text-sm opacity-70">Admins only.</div>;
  }

  return (
    <div className="max-w-6xl mx-auto p-6 lg:p-10" data-testid="admin-satellites-page">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div className="min-w-0">
          <div className="text-[10px] font-mono uppercase tracking-wider opacity-70">Mission Control</div>
          <h1 className="font-display text-3xl">Satellite Network</h1>
          <p className="text-sm opacity-70 mt-1">
            Live view of every regional Social Pulse. Auto-refreshes every 30s.
            {generatedAt && <span className="ml-2 font-mono text-[11px] opacity-70">Last probe {new Date(generatedAt).toLocaleTimeString()}</span>}
          </p>
        </div>
        <div className="flex gap-2 shrink-0">
          <button onClick={load} disabled={busy} className="wofy-btn-ghost text-sm" data-testid="satellites-refresh">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            <span className="ml-1">Refresh</span>
          </button>
          <button onClick={() => setShowAdd((v) => !v)} className="wofy-btn-primary text-sm" data-testid="satellites-add">
            <Plus className="w-4 h-4" /> Register satellite
          </button>
        </div>
      </div>

      {showAdd && (
        <form onSubmit={add} className="rounded-xl border p-4 mb-6 grid gap-3 sm:grid-cols-3" style={{ borderColor: "var(--wofy-border)" }} data-testid="satellites-add-form">
          <input className="wofy-input" placeholder="Display name (e.g. Kerala Pulse)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="satellite-name-input" />
          <input className="wofy-input" placeholder="Region (e.g. Kerala)" value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} data-testid="satellite-region-input" />
          <input className="wofy-input" placeholder="https://kerala.socialpulse.com" value={form.base_url} onChange={(e) => setForm({ ...form, base_url: e.target.value })} data-testid="satellite-url-input" />
          <div className="sm:col-span-3 flex justify-end gap-2">
            <button type="button" onClick={() => setShowAdd(false)} className="wofy-btn-ghost text-sm">Cancel</button>
            <button type="submit" className="wofy-btn-primary text-sm" data-testid="satellite-add-submit">Register</button>
          </div>
        </form>
      )}

      {rows.length === 0 && !busy ? (
        <div className="rounded-xl border-2 border-dashed p-12 text-center" style={{ borderColor: "var(--wofy-border)" }} data-testid="satellites-empty">
          <Globe2 className="w-8 h-8 mx-auto mb-3 opacity-50" />
          <div className="font-medium">No satellites registered yet.</div>
          <p className="text-sm opacity-70 mt-1">
            Deploy a regional Social Pulse with <code className="font-mono">SATELLITE_MODE=1</code> and click <b>Register satellite</b> above to add it to the mission-control grid.
          </p>
        </div>
      ) : (
        <>
          <CombinedTrafficChart rows={rows} />
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3" data-testid="satellites-grid">
            {rows.map((s) => (
              <SatelliteCard key={s.id} s={s} onRemove={() => remove(s.id, s.name)} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}


function SatelliteCard({ s, onRemove }) {
  const h = s.health || {};
  // A registered node might be running in satellite mode (proxies to a
  // central) OR it might itself be a central/regular deployment. Only
  // flag "central down" when the node is *actually* configured as a
  // satellite AND its upstream probe returned false.
  const isSatellite = h.satellite_mode === true;
  const centralDown = isSatellite && h.central_reachable === false;
  const badge = !s.reachable
    ? { color: "#B23A3A", bg: "#FEE2E2", Icon: XCircle, label: "Unreachable" }
    : centralDown
      ? { color: "#B45309", bg: "#FEF3C7", Icon: AlertTriangle, label: "Central down" }
      : { color: "#065F46", bg: "#D1FAE5", Icon: CheckCircle2, label: "Healthy" };
  const Icon = badge.Icon;

  const hitPct = Math.round((h.cache_hit_rate || 0) * 100);

  return (
    <div className="rounded-xl border p-4 bg-white" style={{ borderColor: "var(--wofy-border)" }} data-testid={`satellite-card-${s.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full font-mono uppercase"
              style={{ color: badge.color, background: badge.bg }}
              data-testid={`satellite-status-${s.id}`}
            >
              <Icon className="w-3 h-3" /> {badge.label}
            </span>
            <span className="text-[10px] font-mono uppercase tracking-wider opacity-70">{s.region}</span>
          </div>
          <div className="font-display text-lg mt-1 truncate" title={s.name}>{s.name}</div>
          <a href={s.base_url} target="_blank" rel="noreferrer" className="text-xs opacity-70 truncate block hover:underline">{s.base_url}</a>
        </div>
        <button onClick={onRemove} className="opacity-40 hover:opacity-100" aria-label="Un-register satellite" data-testid={`satellite-remove-${s.id}`}>
          <Trash2 className="w-4 h-4" style={{ color: "#B23A3A" }} />
        </button>
      </div>

      {s.reachable ? (
        <div className="grid grid-cols-2 gap-2 mt-4 text-xs">
          <Stat label="Mode" value={isSatellite ? "Satellite" : "Central"} />
          <Stat label="Cache hit rate" value={`${hitPct}%`} />
          <Stat label="Requests / hr" value={h.requests_last_hour ?? "—"} />
          <Stat label="Upstream errors" value={h.upstream_errors ?? 0} bad={(h.upstream_errors || 0) > 0} />
          <Stat label="Cache entries" value={h.cache_size ?? 0} />
          <Stat label="Region filter" value={(h.region_filter || []).join(", ") || "—"} span={2} />
          <div className="col-span-2 pt-2 mt-1 border-t" style={{ borderColor: "var(--wofy-border)" }} data-testid={`satellite-sparkline-${s.id}`}>
            <div className="flex items-center justify-between mb-1">
              <div className="text-[9px] font-mono uppercase opacity-60">Traffic · last 24h (hourly)</div>
              <div className="text-[9px] font-mono opacity-60">
                peak {Math.max(0, ...(h.requests_hourly_24h || [0]))} · total {(h.requests_hourly_24h || []).reduce((a, b) => a + b, 0)}
              </div>
            </div>
            <Sparkline data={h.requests_hourly_24h || Array(24).fill(0)} height={38} color={badge.color} />
          </div>
          {h.last_central_ok_iso && (
            <Stat label="Last central OK" value={new Date(h.last_central_ok_iso).toLocaleTimeString()} span={2} />
          )}
          {h.last_central_error && (
            <div className="col-span-2 text-[11px] p-2 rounded" style={{ background: "#FEE2E2", color: "#7C2D12" }}>
              Last error: {h.last_central_error}
            </div>
          )}
        </div>
      ) : (
        <div className="mt-4 text-xs p-2 rounded" style={{ background: "#FEE2E2", color: "#7C2D12" }}>
          {s.probe_error || `HTTP ${s.status_code}`}
        </div>
      )}
    </div>
  );
}


function Stat({ label, value, span = 1, bad = false }) {
  return (
    <div style={{ gridColumn: `span ${span}` }}>
      <div className="text-[9px] font-mono uppercase opacity-60">{label}</div>
      <div className={`text-sm font-medium ${bad ? "text-red-600" : ""}`}>{value}</div>
    </div>
  );
}


/**
 * Sparkline — tiny SVG bar chart for 24 hourly buckets. Bars grow from
 * the bottom so at-a-glance the tallest bar wins the visitor's eye,
 * which is exactly what you want when spotting demand spikes.
 */
function Sparkline({ data, height = 40, color = "#065F46" }) {
  const values = Array.isArray(data) && data.length ? data : Array(24).fill(0);
  const max = Math.max(1, ...values);   // avoid divide-by-zero
  const barW = 100 / values.length;
  return (
    <svg viewBox={`0 0 100 ${height}`} preserveAspectRatio="none" width="100%" height={height} role="img" aria-label="Hourly request bar chart for the last 24 hours">
      {values.map((v, i) => {
        const h = Math.max(1, (v / max) * (height - 2));
        return (
          <rect
            key={i}
            x={i * barW + 0.4}
            y={height - h}
            width={Math.max(0.5, barW - 0.8)}
            height={h}
            fill={v > 0 ? color : "#E5E7EB"}
            opacity={v > 0 ? 0.85 : 0.5}
          >
            <title>{`${23 - i}h ago: ${v} req${v === 1 ? "" : "s"}`}</title>
          </rect>
        );
      })}
    </svg>
  );
}


/**
 * CombinedTrafficChart — one big line chart overlaying every satellite's
 * 24-hour request curve. Uses a stable colour palette so each row keeps
 * its colour between polls. Legend on the right.
 */
function CombinedTrafficChart({ rows }) {
  const PALETTE = ["#B25D38", "#5C6B52", "#2563EB", "#B45309", "#7C3AED", "#059669", "#DC2626", "#0891B2", "#DB2777", "#4338CA"];
  const series = rows
    .filter((s) => s.reachable && Array.isArray(s.health?.requests_hourly_24h))
    .map((s, i) => ({
      id: s.id,
      name: s.name,
      region: s.region,
      color: PALETTE[i % PALETTE.length],
      data: s.health.requests_hourly_24h,
    }));

  if (!series.length) return null;

  const W = 800;
  const H = 180;
  const P = { top: 12, right: 12, bottom: 22, left: 32 };
  const chartW = W - P.left - P.right;
  const chartH = H - P.top - P.bottom;
  const max = Math.max(1, ...series.flatMap((s) => s.data));
  const stepX = chartW / 23;

  const yTicks = [0, Math.ceil(max / 2), max];

  const linePath = (data) =>
    data.map((v, i) => `${i === 0 ? "M" : "L"}${P.left + i * stepX} ${P.top + chartH - (v / max) * chartH}`).join(" ");

  return (
    <div className="rounded-xl border p-4 mb-4 bg-white" style={{ borderColor: "var(--wofy-border)" }} data-testid="satellites-combined-chart">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-wider opacity-70">Global · last 24h</div>
          <div className="font-display text-lg leading-tight">Request Traffic</div>
        </div>
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px]" data-testid="satellites-chart-legend">
          {series.map((s) => (
            <div key={s.id} className="flex items-center gap-1.5">
              <span className="inline-block w-3 h-[3px] rounded-full" style={{ background: s.color }} />
              <span className="opacity-80">{s.region}</span>
              <span className="font-mono opacity-50">· {s.data.reduce((a, b) => a + b, 0)}</span>
            </div>
          ))}
        </div>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" preserveAspectRatio="xMidYMid meet" role="img" aria-label="Combined per-region traffic chart for the last 24 hours">
        {/* y-axis grid + labels */}
        {yTicks.map((t, i) => {
          const y = P.top + chartH - (t / max) * chartH;
          return (
            <g key={i}>
              <line x1={P.left} x2={W - P.right} y1={y} y2={y} stroke="#E5E7EB" strokeDasharray="2 3" />
              <text x={P.left - 6} y={y + 3} fontSize="9" fill="#6B7280" textAnchor="end">{t}</text>
            </g>
          );
        })}
        {/* x-axis labels every 6h */}
        {[0, 6, 12, 18, 23].map((i) => (
          <text key={i} x={P.left + i * stepX} y={H - 6} fontSize="9" fill="#6B7280" textAnchor="middle">
            {23 - i === 0 ? "now" : `-${23 - i}h`}
          </text>
        ))}
        {/* series */}
        {series.map((s) => (
          <g key={s.id}>
            <path d={linePath(s.data)} fill="none" stroke={s.color} strokeWidth="1.75" strokeLinejoin="round" strokeLinecap="round" opacity="0.9" />
            {s.data.map((v, i) => (
              <circle key={i} cx={P.left + i * stepX} cy={P.top + chartH - (v / max) * chartH} r={v > 0 ? 2 : 0} fill={s.color}>
                <title>{`${s.name} · ${23 - i}h ago: ${v} req${v === 1 ? "" : "s"}`}</title>
              </circle>
            ))}
          </g>
        ))}
      </svg>
    </div>
  );
}
